import type { Deposit } from '../fianza/types'
import type { PaymentSummary } from '../pagos/types'
import type { Dispositive, GuideItem, Stay } from '../reserva/types'
import type { Access, AccessKind, KeyMissingReason, KeyStatus } from './types'

const LOCK_TYPES = new Set(['puerta', 'portal', 'rellano', 'garaje'])
const KIND_BY_TYPE: Record<string, AccessKind> = { portal: 'portal', puerta: 'door', rellano: 'landing', garaje: 'garage' }
const ORDER: AccessKind[] = ['portal', 'garage', 'landing', 'door', 'other']

export const isLock = (d: Dispositive) => LOCK_TYPES.has(d.type)
export const openCommand = (d: Dispositive) => d.commands.find((c) => c.type === 'abrir') ?? null

export function toAccesses(dispositives: Dispositive[]): Access[] {
  return dispositives
    .filter(isLock)
    .map((d): Access => ({
      id: d.id,
      name: d.name,
      kind: KIND_BY_TYPE[d.type] ?? 'other',
      openable: openCommand(d) !== null,
      order: 0,
      messageBeforeUse: openCommand(d)?.messageBeforeUse ?? null,
      intercom: d.intercom,
      code: null,
    }))
    .sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind))
    .map((a, i) => ({ ...a, order: i }))
}

/**
 * Estado de la llave derivado de E1 (inventario D6, confirmado): `active` SOLO si `showDomotic`;
 * `expired` tras la estancia; `none` si no hay cerradura conectada; si no, `locked`.
 */
export function deriveKeyStatus(stay: Pick<Stay, 'dispositives' | 'flags' | 'dates' | 'phase'>): KeyStatus {
  const accesses = toAccesses(stay.dispositives)
  if (accesses.length === 0) return { state: 'none', activeFrom: null, activeUntil: null, accesses }
  const state = stay.phase === 'after' || stay.phase === 'cancelled' ? 'expired' : stay.flags.showDomotic ? 'active' : 'locked'
  return { state, activeFrom: stay.dates.checkIn, activeUntil: stay.dates.checkOut, accesses }
}

/**
 * Por qué está bloqueada (inventario D6): registro pendiente, fianza sin reservar, pago pendiente con
 * pasarela, o aún no es la hora. Si el cliente no encuentra ningún motivo devuelve [] y la pantalla
 * muestra el motivo genérico con el contacto; nunca uno inventado (CLAUDE.md).
 */
export function deriveKeyMissing(
  stay: Pick<Stay, 'registration' | 'dates' | 'flags'>,
  payment: PaymentSummary | null,
  deposit: Deposit | null,
  now: Date = new Date(),
): KeyMissingReason[] {
  const missing: KeyMissingReason[] = []
  if (stay.registration.pending > 0) missing.push({ code: 'register', count: stay.registration.pending })
  if (deposit && deposit.status === 'pending') missing.push({ code: 'deposit', amount: deposit.amount })
  if (payment && stay.flags.paidWithGateway && payment.pending.amount > 0 && !(payment.rest?.paid ?? false)) missing.push({ code: 'payment', amount: payment.pending })
  if (now < new Date(stay.dates.checkIn)) missing.push({ code: 'time', activeFrom: stay.dates.checkIn })
  return missing
}

/**
 * Ítem de la guía (`manualList`) que explica cómo entrar, para la propiedad sin cerradura conectada con la
 * API actual (inventario D40, heurística como la de clima en D13). Se muestra tal cual: no se parte en pasos.
 */
const ENTRY_GUIDE_RE = /acce(so|der)|llegada|entrada|entrar|llave|check.?in/i
export const entryGuideItem = (stay: Pick<Stay, 'guide'>): GuideItem | null => stay.guide.manual.find((it) => ENTRY_GUIDE_RE.test(it.name)) ?? null
